import React, { useState, useRef, useCallback } from "react";
import {
  CloudArrowUpIcon,
  PhotoIcon,
  XMarkIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import LoadingSpinner from "../LoadingSpinner";

const AvatarUpload = ({ currentAvatar, onAvatarUpdate, userName }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [cropData, setCropData] = useState(null);
  const [showCropModal, setShowCropModal] = useState(false);
  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
  const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];

  // Generate initials for default avatar
  const getInitials = (name) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const validateFile = (file) => {
    if (!file) return { valid: false, error: "No file selected" };

    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: "Please select a valid image file (JPEG, PNG, GIF, or WebP)",
      };
    }

    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: "File size must be less than 5MB" };
    }

    return { valid: true };
  };

  const handleFileSelect = useCallback((file) => {
    const validation = validateFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setCropData({
          file,
          imageUrl: e.target.result,
          originalWidth: img.width,
          originalHeight: img.height,
        });
        setShowCropModal(true);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        handleFileSelect(files[0]);
      }
    },
    [handleFileSelect],
  );

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const cropImage = (imageUrl, cropArea) => {
    return new Promise((resolve) => {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        // Set canvas size to desired output (square)
        const size = 200;
        canvas.width = size;
        canvas.height = size;

        // Calculate crop dimensions
        const scaleX = img.width / cropData.originalWidth;
        const scaleY = img.height / cropData.originalHeight;

        // Draw cropped image
        ctx.drawImage(
          img,
          cropArea.x * scaleX,
          cropArea.y * scaleY,
          cropArea.width * scaleX,
          cropArea.height * scaleY,
          0,
          0,
          size,
          size,
        );

        // Convert to blob
        canvas.toBlob(resolve, "image/jpeg", 0.9);
      };

      img.src = imageUrl;
    });
  };

  const handleCropConfirm = async () => {
    if (!cropData) return;

    setIsUploading(true);
    try {
      // Simple center crop for now (can be enhanced with proper crop tool)
      const size = Math.min(cropData.originalWidth, cropData.originalHeight);
      const x = (cropData.originalWidth - size) / 2;
      const y = (cropData.originalHeight - size) / 2;

      const croppedBlob = await cropImage(cropData.imageUrl, {
        x,
        y,
        width: size,
        height: size,
      });

      // Convert blob to base64 for API
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result.split(",")[1];
        setPreview(e.target.result);
        await onAvatarUpdate(base64Data);
        setShowCropModal(false);
        setCropData(null);
        setIsUploading(false);
      };
      reader.readAsDataURL(croppedBlob);
    } catch (error) {
      console.error("Error cropping image:", error);
      alert("Error processing image. Please try again.");
      setIsUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    if (window.confirm("Are you sure you want to remove your profile picture?")) {
      setIsUploading(true);
      try {
        await onAvatarUpdate(null);
        setPreview(null);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const currentAvatarUrl = preview || currentAvatar;

  return (
    <div className="flex items-start space-x-6">
      {/* Avatar Display */}
      <div className="relative">
        {currentAvatarUrl ? (
          <img
            src={currentAvatarUrl}
            alt="Profile"
            className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
          />
        ) : (
          <div className="w-24 h-24 rounded-full bg-primary-100 border-2 border-gray-200 flex items-center justify-center">
            <span className="text-2xl font-semibold text-primary-600">
              {getInitials(userName)}
            </span>
          </div>
        )}

        {isUploading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
            <LoadingSpinner size="sm" />
          </div>
        )}
      </div>

      {/* Upload Controls */}
      <div className="flex-1">
        <div className="flex items-center space-x-3 mb-2">
          <button
            onClick={() => fileInputRef.current?.click()}
            className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
            disabled={isUploading}
          >
            Upload Photo
          </button>

          {currentAvatarUrl && (
            <button
              onClick={handleRemoveAvatar}
              className="text-sm text-red-600 hover:text-red-800 font-medium transition-colors"
              disabled={isUploading}
            >
              Remove
            </button>
          )}
        </div>

        {/* Drag and Drop Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-4 transition-colors ${
            dragActive
              ? "border-primary-400 bg-primary-50"
              : "border-gray-300 hover:border-gray-400"
          }`}
          onDrop={handleDrop}
          onDragOver={handleDrag}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
        >
          <div className="text-center">
            <CloudArrowUpIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
            <div className="text-sm text-gray-600">
              <span
                className="font-medium text-primary-600 hover:text-primary-500 cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                Click to upload
              </span>{" "}
              or drag and drop
            </div>
            <p className="text-xs text-gray-500 mt-1">
              PNG, JPG, GIF or WebP up to 5MB
            </p>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          accept={ALLOWED_TYPES.join(",")}
          onChange={(e) => {
            if (e.target.files?.[0]) {
              handleFileSelect(e.target.files[0]);
            }
          }}
        />
      </div>

      {/* Hidden canvas for image processing */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Crop Modal */}
      {showCropModal && cropData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Crop Profile Picture
              </h3>
              <button
                onClick={() => {
                  setShowCropModal(false);
                  setCropData(null);
                }}
                className="text-gray-400 hover:text-gray-600"
                disabled={isUploading}
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <img
                src={cropData.imageUrl}
                alt="Crop preview"
                className="w-full h-64 object-contain border border-gray-200 rounded"
              />
              <p className="text-sm text-gray-500 mt-2">
                Image will be automatically cropped to a square format.
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowCropModal(false);
                  setCropData(null);
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                disabled={isUploading}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
              >
                {isUploading ? (
                  <>
                    <LoadingSpinner size="sm" />
                    <span>Processing...</span>
                  </>
                ) : (
                  <>
                    <CheckIcon className="w-4 h-4" />
                    <span>Use Photo</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AvatarUpload;
